import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, ArrowRight, AlertCircle, Eye, EyeOff } from "lucide-react";
import { verifyCredentials, setSession } from "@/lib/auth";

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const ok = await verifyCredentials(username.trim(), password);
      if (!ok) {
        setError("Invalid credentials. Double-check the casing and symbols.");
        setLoading(false);
        return;
      }
      await setSession();
      navigate(from, { replace: true });
    } catch {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col lg:flex-row">
      {/* Left brand panel */}
      <aside className="hidden lg:flex flex-col justify-between w-[44%] xl:w-[40%] border-r-2 border-foreground p-12 relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
            BRAC Bank
          </p>
          <h1 className="font-display font-black text-4xl xl:text-5xl leading-none mt-1">
            CMS<span className="text-brut-yellow">.</span>docs
          </h1>
          <p className="font-mono text-[10px] mt-1 text-muted-foreground">
            v1.1 · interactive tutorials
          </p>
        </div>

        <div className="relative">
          <div className="brut-border brut-shadow-lg bg-brut-yellow px-5 py-4 inline-block -rotate-2">
            <p className="font-mono text-[10px] uppercase tracking-widest">
              Restricted area
            </p>
            <p className="font-display font-bold text-base mt-1 leading-tight max-w-[260px]">
              Sign in with your provided learner credentials to access the
              full manual.
            </p>
          </div>
        </div>

        <div className="relative font-mono text-[11px] text-muted-foreground">
          Need access? Email{" "}
          <a className="underline" href="mailto:info@singularitybd.com">
            info@singularitybd.com
          </a>
        </div>
      </aside>

      {/* Right form */}
      <main className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="w-full max-w-md bg-background brut-border brut-shadow-lg p-6 sm:p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 brut-border bg-brut-yellow flex items-center justify-center">
              <Lock className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Step 1
              </p>
              <h2 className="font-display font-black text-2xl leading-none">
                Sign in
              </h2>
            </div>
          </div>

          <label className="block mb-5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Username
            </span>
            <input
              type="text"
              autoComplete="username"
              data-testid="login-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              required
              placeholder="your-learner-id"
              className="w-full mt-1.5 brut-border bg-background px-3 py-3 text-base font-mono focus:outline-none focus:bg-brut-yellow/30"
            />
          </label>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Password
            </span>
            <div className="relative mt-1.5">
              <input
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full brut-border bg-background pl-3 pr-11 py-3 text-base font-mono focus:outline-none focus:bg-brut-yellow/30"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="login-toggle-password"
                className="absolute right-1.5 top-1.5 h-9 w-9 flex items-center justify-center brut-border bg-background hover:bg-muted"
              >
                {showPw ? (
                  <EyeOff className="w-4 h-4" strokeWidth={2.5} />
                ) : (
                  <Eye className="w-4 h-4" strokeWidth={2.5} />
                )}
              </button>
            </div>
          </label>

          {error && (
            <div
              data-testid="login-error"
              className="mt-4 brut-border bg-brut-orange p-3 flex items-start gap-2"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
              <p className="text-sm font-semibold leading-snug">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full mt-6 inline-flex items-center justify-center gap-2 brut-border brut-shadow brut-hover bg-foreground text-background px-5 py-4 font-bold uppercase tracking-wider text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Verifying…" : "Enter the manual"}
            <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </button>

          <p className="text-[11px] text-muted-foreground mt-5 font-mono leading-relaxed">
            By signing in you confirm you are an authorised BRAC Bank CMS
            learner. Sessions persist locally on this device.
          </p>
        </form>
      </main>
    </div>
  );
};
